/**
 * AI forecast card (2026-09-30). The numbers are computed by the forecasting
 * engine (backend app/services/forecast.py) — finish date and cost ranges,
 * drivers, people, what-if scenarios — and AI explains them and recommends
 * actions. The track record shows how past forecasts compared with reality.
 */
import { useCallback, useEffect, useState } from 'react'
import { Calendar, DollarSign, Gauge, RefreshCw, Sparkles, Target, Users } from 'lucide-react'
import {
  aiPredictionsService, type ForecastAccuracy, type PredictionBundle, type PredictionRow,
} from '../../services/aiPredictions'

interface Props {
  entityType: 'project' | 'portfolio' | 'program'
  entityId: number
}

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const abs = Math.abs(v)
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (abs >= 10_000) return `$${Math.round(v / 1000)}k`
  return `$${Math.round(v).toLocaleString()}`
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86_400_000)
}

export function bundleFromRows(rows: PredictionRow[], entityType: string, entityId: number): PredictionBundle {
  const eta = rows.find((r) => r.type === 'completion_date')
  const bac = rows.find((r) => r.type === 'budget')
  const res = rows.find((r) => r.type === 'resource_needs')
  const details = eta?.details ?? undefined
  let needs: PredictionBundle['resource_needs'] = []
  try {
    const parsed = res ? JSON.parse(res.predicted_value) : []
    needs = Array.isArray(parsed) ? parsed : []
  } catch { needs = [] }
  return {
    entity_type: entityType, entity_id: entityId,
    completion_date: eta?.predicted_value ?? null,
    completion_date_low: details?.completion_date_low ?? null,
    completion_date_high: details?.completion_date_high ?? null,
    completion_date_confidence: eta?.confidence ?? null,
    budget_at_completion: bac ? Number(bac.predicted_value) : null,
    budget_low: details?.budget_low ?? null,
    budget_high: details?.budget_high ?? null,
    budget_confidence: bac?.confidence ?? null,
    resource_needs: needs,
    details,
    generated_at: eta?.created_at ?? rows[0]?.created_at ?? new Date().toISOString(),
    ai_powered: Boolean(details?.ai_powered),
  }
}

export function PredictionCard({ entityType, entityId }: Props) {
  const [bundle, setBundle] = useState<PredictionBundle | null>(null)
  const [accuracy, setAccuracy] = useState<ForecastAccuracy | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // "empty" = nothing forecast yet: a normal state with a Generate action.
  const [empty, setEmpty] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setEmpty(false)
    try {
      const rows = await aiPredictionsService.list(entityType, entityId)
      if (rows.length === 0) {
        setBundle(null)
        setEmpty(true)
      } else {
        setBundle(bundleFromRows(rows, entityType, entityId))
      }
    } catch (e) {
      console.error('PredictionCard: failed to load forecast', e)
      setError('Couldn’t load the forecast.')
    } finally {
      setLoading(false)
    }
    aiPredictionsService.accuracy().then(setAccuracy).catch(() => setAccuracy(null))
  }, [entityType, entityId])

  const generate = async () => {
    setLoading(true)
    setError(null)
    try {
      setBundle(await aiPredictionsService.generate(entityType, entityId))
      setEmpty(false)
    } catch (e) {
      console.error('PredictionCard: failed to generate forecast', e)
      setError('Couldn’t produce a forecast. There may not be enough data yet.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [load])

  const d = bundle?.details
  const planned = d?.planned_end ?? null
  const late = bundle?.completion_date && planned ? daysBetween(bundle.completion_date, planned) : null
  const budget = d?.cost?.budget ?? null
  const over = bundle?.budget_at_completion != null && budget ? bundle.budget_at_completion - budget : null
  const explanation = d?.explanation
  const drivers = explanation?.drivers?.length ? explanation.drivers : (d?.drivers ?? []).map((x) => x.text)
  const track = accuracy?.completion_date

  return (
    <section aria-label="AI forecast" className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Sparkles className="h-4 w-4 text-primary" /> AI forecast
          </h3>
          <p className="text-xs text-muted-foreground">
            Computed from this {entityType}'s tasks, dependencies and spend{bundle?.ai_powered ? ', explained by AI' : ''}.
          </p>
        </div>
        {bundle && (
          <button type="button" onClick={generate} disabled={loading}
            className="flex min-h-[32px] items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Update forecast
          </button>
        )}
      </div>

      {loading && !bundle && <p className="text-xs text-muted-foreground">Loading forecast…</p>}

      {error && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-destructive">{error}</p>
          <button type="button" onClick={bundle || !empty ? generate : load} disabled={loading}
            className="flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Retry
          </button>
        </div>
      )}

      {!error && empty && !loading && (
        <div className="py-3 text-center">
          <p className="mb-2 text-xs text-muted-foreground">
            No forecast yet. It uses the {entityType}'s tasks, dates and spend — the more work is tracked, the sharper it gets.
          </p>
          <button type="button" onClick={generate} disabled={loading}
            className="inline-flex min-h-[36px] items-center gap-1 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
            <Sparkles className="h-3 w-3" /> Generate forecast
          </button>
        </div>
      )}

      {bundle && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
            <Stat icon={<Calendar className="h-4 w-4 text-primary" />} label="Likely finish"
              value={bundle.completion_date ?? '—'}
              hint={[
                bundle.completion_date_low && bundle.completion_date_high ? `${bundle.completion_date_low} – ${bundle.completion_date_high}` : '',
                late !== null ? (late > 0 ? `${late} days after plan` : late < 0 ? `${-late} days early` : 'on the planned date') : '',
              ].filter(Boolean).join(' · ')}
              tone={late !== null && late > 7 ? 'bad' : late !== null && late > 0 ? 'warn' : undefined} />
            <Stat icon={<DollarSign className="h-4 w-4 text-primary" />} label="Cost at completion"
              value={money(bundle.budget_at_completion)}
              hint={[
                bundle.budget_low != null && bundle.budget_high != null ? `${money(bundle.budget_low)} – ${money(bundle.budget_high)}` : '',
                over !== null ? (over > 0 ? `${money(over)} over budget` : `${money(-over)} under budget`) : '',
              ].filter(Boolean).join(' · ')}
              tone={over !== null && budget ? (over / budget > 0.1 ? 'bad' : over > 0 ? 'warn' : undefined) : undefined} />
            <Stat icon={<Gauge className="h-4 w-4 text-primary" />} label="Confidence"
              value={bundle.completion_date_confidence != null ? `${Math.round(bundle.completion_date_confidence * 100)}%` : '—'}
              hint={d?.completed_last_8_weeks != null ? `${d.completed_last_8_weeks} tasks finished in the last 8 weeks` : ''} />
          </div>

          {explanation?.summary && <p className="text-sm text-foreground">{explanation.summary}</p>}

          {drivers.length > 0 && (
            <div>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why</h4>
              <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">
                {drivers.slice(0, 4).map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          )}

          {explanation?.recommendations && explanation.recommendations.length > 0 && (
            <div>
              <h4 className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Target className="h-3 w-3" /> Recommended
              </h4>
              <ul className="space-y-1 text-sm">
                {explanation.recommendations.map((r) => (
                  <li key={r.action}><span className="font-medium text-foreground">{r.action}</span>
                    <span className="text-muted-foreground"> — {r.why}</span></li>
                ))}
              </ul>
            </div>
          )}

          {d?.scenarios && d.scenarios.length > 0 && (
            <div className="flex flex-wrap gap-2" aria-label="What if">
              {d.scenarios.map((s) => (
                <span key={s.name} className="rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-foreground">
                  {s.name}: {s.finish}{s.days_saved > 0 ? ` (${s.days_saved} days sooner)` : ' (no change)'}
                </span>
              ))}
            </div>
          )}

          {bundle.resource_needs && bundle.resource_needs.length > 0 && (
            <div>
              <h4 className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Users className="h-3 w-3" /> People until finish
              </h4>
              <ul className="flex flex-wrap gap-2 text-xs">
                {bundle.resource_needs.slice(0, 5).map((r) => (
                  <li key={r.role} className={`rounded px-2 py-1 ${r.fte > 1 ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground'}`}>
                    {r.role}: {r.fte} FTE{r.fte > 1 ? ' — over-committed' : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-muted-foreground" aria-label="Track record">
            {track && track.scored >= 3
              ? `Track record: ${track.hit ?? 0} of ${track.scored} past finish forecasts landed within a week` +
                (track.mean_abs_error != null ? ` (average miss ${track.mean_abs_error} days).` : '.')
              : 'Track record: each forecast is checked against the real finish date as projects complete.'}
          </p>
        </div>
      )}
    </section>
  )
}

function Stat({ icon, label, value, hint, tone }: {
  icon: React.ReactNode; label: string; value: string; hint: string; tone?: 'warn' | 'bad'
}) {
  const valueCls = tone === 'bad' ? 'text-destructive' : tone === 'warn' ? 'text-warning' : 'text-foreground'
  return (
    <div className="rounded border border-border bg-background p-2">
      <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">{icon}{label}</div>
      <div className={`truncate text-sm font-semibold ${valueCls}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}
